/**
 * Sales Upload History Page
 * ==========================
 * Admin view for all menu upload/extraction history
 *
 * Features:
 * - View all uploads across the system
 * - Filter by status, date, sales rep
 * - See extraction stats and processing times
 * - Track claimed vs unclaimed uploads
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  FileText,
  Loader2,
  AlertCircle,
  CheckCircle,
  XCircle,
  Clock,
  ChevronLeft,
  ChevronRight,
  Filter,
} from 'lucide-react';
import { salesApi, UploadHistoryResponse, UploadRecord } from '../services/api/salesApi';

const SalesUploadHistory: React.FC = () => {
  const [data, setData] = useState<UploadHistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await salesApi.getUploadHistory({
        page,
        limit: 20,
        status_filter: statusFilter === 'all' ? undefined : statusFilter,
      });
      setData(response);
    } catch (err: any) {
      console.error('Error fetching upload history:', err);
      setError(err.response?.data?.detail || 'Failed to load upload history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, [page, statusFilter]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return (
          <span className="px-2 py-1 text-xs rounded-full bg-green-500/20 text-green-400 flex items-center gap-1">
            <CheckCircle className="w-3 h-3" />
            Completed
          </span>
        );
      case 'failed':
        return (
          <span className="px-2 py-1 text-xs rounded-full bg-red-500/20 text-red-400 flex items-center gap-1">
            <XCircle className="w-3 h-3" />
            Failed
          </span>
        );
      case 'processing':
        return (
          <span className="px-2 py-1 text-xs rounded-full bg-yellow-500/20 text-yellow-400 flex items-center gap-1">
            <Loader2 className="w-3 h-3 animate-spin" />
            Processing
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 text-xs rounded-full bg-gray-500/20 text-gray-400 flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Pending
          </span>
        );
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white">Upload History</h1>
        <p className="text-white/70 mt-1">View all menu uploads and extraction results</p>
      </div>

      {/* Stats Cards */}
      {data?.stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <div className="bg-white/10 rounded-lg p-4">
            <div className="text-2xl font-bold text-white">{data.stats.total_uploads}</div>
            <div className="text-sm text-white/70">Total Uploads</div>
          </div>
          <div className="bg-white/10 rounded-lg p-4">
            <div className="text-2xl font-bold text-green-400">{data.stats.completed}</div>
            <div className="text-sm text-white/70">Completed</div>
          </div>
          <div className="bg-white/10 rounded-lg p-4">
            <div className="text-2xl font-bold text-red-400">{data.stats.failed}</div>
            <div className="text-sm text-white/70">Failed</div>
          </div>
          <div className="bg-white/10 rounded-lg p-4">
            <div className="text-2xl font-bold text-blue-400">{data.stats.claimed}</div>
            <div className="text-sm text-white/70">Claimed</div>
          </div>
          <div className="bg-white/10 rounded-lg p-4">
            <div className="text-2xl font-bold text-white">{data.stats.avg_items}</div>
            <div className="text-sm text-white/70">Avg Items</div>
          </div>
          <div className="bg-white/10 rounded-lg p-4">
            <div className="text-2xl font-bold text-white">{data.stats.avg_time_seconds.toFixed(1)}s</div>
            <div className="text-sm text-white/70">Avg Time</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-white/70" />
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#FDB913]"
          >
            <option value="all">All Status</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
            <option value="processing">Processing</option>
            <option value="pending">Pending</option>
          </select>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 text-[#FDB913] animate-spin" />
        </div>
      ) : error ? (
        <div className="bg-red-500/20 text-red-400 p-4 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {error}
        </div>
      ) : (
        <div className="bg-white/5 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-white/10">
                  <th className="px-4 py-3 text-left text-xs font-medium text-white/70 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white/70 uppercase">Restaurant</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white/70 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white/70 uppercase">Items</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white/70 uppercase">Time</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white/70 uppercase">Files</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white/70 uppercase">Sales Rep</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white/70 uppercase">Claimed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {data?.uploads.map((upload, index) => (
                  <motion.tr
                    key={upload.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.03 }}
                    className="hover:bg-white/5"
                  >
                    <td className="px-4 py-3 text-sm text-white/80">
                      {upload.created_at ? formatDate(upload.created_at) : 'N/A'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-white">{upload.restaurant_name}</div>
                      <div className="text-xs text-white/60">{upload.location}</div>
                    </td>
                    <td className="px-4 py-3">{getStatusBadge(upload.status)}</td>
                    <td className="px-4 py-3 text-sm text-white/80">{upload.items_extracted}</td>
                    <td className="px-4 py-3 text-sm text-white/80">{upload.processing_time_seconds.toFixed(1)}s</td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-white/80">{upload.file_count} file(s)</div>
                      <div className="text-xs text-white/60">{upload.file_size_mb} MB</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-white/80">
                      {upload.sales_rep.split('@')[0]}
                    </td>
                    <td className="px-4 py-3">
                      {upload.claimed ? (
                        <span className="text-green-400 text-sm">Yes</span>
                      ) : (
                        <span className="text-white/50 text-sm">No</span>
                      )}
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {data?.pagination && (
            <div className="flex items-center justify-between px-4 py-3 bg-white/5">
              <div className="text-sm text-white/70">
                Showing {((data.pagination.page - 1) * data.pagination.limit) + 1} to{' '}
                {Math.min(data.pagination.page * data.pagination.limit, data.pagination.total)} of{' '}
                {data.pagination.total} uploads
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="p-2 rounded-lg bg-white/10 text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/20"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm text-white">
                  Page {data.pagination.page} of {data.pagination.total_pages}
                </span>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page >= data.pagination.total_pages}
                  className="p-2 rounded-lg bg-white/10 text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/20"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SalesUploadHistory;
